import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Building2, User, Phone, Mail, FileText, DollarSign } from 'lucide-react';
import { formatNzPhone } from '@/lib/formatters';

const TABS = ['Companies', 'Assessors', 'Claims'];
const PHONE_KEYS = new Set(['phone', 'mobile']);

export default function InsurancePage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('Companies');
  const [companies, setCompanies] = useState([]);
  const [assessors, setAssessors] = useState([]);
  const [claims, setClaims] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [formType, setFormType] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [co, as, cl] = await Promise.all([
        api.get('/insurance/companies'),
        api.get('/insurance/assessors'),
        api.get('/insurance/claims'),
      ]);
      setCompanies(co.data);
      setAssessors(as.data);
      setClaims(cl.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openForm = (type, data = {}) => {
    setFormType(type);
    setForm(data);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (formType === 'company') {
        if (form._id) await api.put(`/insurance/companies/${form._id}`, form);
        else await api.post('/insurance/companies', form);
      } else if (formType === 'assessor') {
        await api.post('/insurance/assessors', form);
      } else if (formType === 'claim') {
        if (form._id) await api.put(`/insurance/claims/${form._id}`, form);
        else await api.post('/insurance/claims', form);
      }
      setShowForm(false);
      setForm({});
      load();
    } catch (e) { console.error(e); }
  };

  const companyFields = [
    { k: 'company_name', l: 'Company Name', r: true }, { k: 'phone', l: 'Phone' },
    { k: 'email', l: 'Email' }, { k: 'website', l: 'Website' },
    { k: 'address_street', l: 'Address' }, { k: 'address_suburb', l: 'Suburb' },
    { k: 'address_city', l: 'City' },
  ];

  const assessorFields = [
    { k: 'name_first', l: 'First Name', r: true }, { k: 'name_last', l: 'Last Name', r: true },
    { k: 'phone', l: 'Phone' }, { k: 'mobile', l: 'Mobile' },
    { k: 'email', l: 'Email' }, { k: 'comments', l: 'Comments' },
  ];

  const claimFields = [
    { k: 'claim_number', l: 'Claim Number', r: true }, { k: 'claim_date', l: 'Date', t: 'date' },
    { k: 'claim_status', l: 'Status', type: 'select', options: ['Open', 'In Progress', 'Approved', 'Declined', 'Closed'] },
    { k: 'claim_amount', l: 'Claim Amount', t: 'number' }, { k: 'approved_amount', l: 'Approved Amount', t: 'number' },
    { k: 'comments', l: 'Comments', t: 'textarea' },
  ];

  return (
    <div data-testid="insurance-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Insurance</h1>
          <p className="text-sm text-slate-500 mt-1">Companies, assessors & claims</p>
        </div>
        <button data-testid="add-insurance-button" onClick={() => openForm(tab === 'Companies' ? 'company' : tab === 'Assessors' ? 'assessor' : 'claim')} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">
          <Plus size={16} /> Add {tab === 'Companies' ? 'Company' : tab === 'Assessors' ? 'Assessor' : 'Claim'}
        </button>
      </div>
      <div className="flex gap-1 border-b border-[#E2E2DF]">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${tab === t ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{t} ({t === 'Companies' ? companies.length : t === 'Assessors' ? assessors.length : claims.length})</button>
        ))}
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <>
          {tab === 'Companies' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {companies.map(c => (
                <div key={c._id} data-testid={`insurance-company-${c._id}`} className="bg-white border border-[#E2E2DF] rounded-md p-4 hover:shadow-sm transition-all cursor-pointer" onClick={() => openForm('company', c)}>
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 size={18} className="text-[#4A6FA5]" />
                    <p className="text-sm font-semibold text-slate-800">{c.company_name}</p>
                  </div>
                  {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone size={11} />{c.phone}</p>}
                  {c.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail size={11} />{c.email}</p>}
                </div>
              ))}
              {companies.length === 0 && <p className="col-span-full text-center py-8 text-slate-400">No insurance companies yet</p>}
            </div>
          )}
          {tab === 'Assessors' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assessors.map(a => (
                <div key={a._id} className="bg-white border border-[#E2E2DF] rounded-md p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <User size={18} className="text-[#D35400]" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{a.name_first} {a.name_last}</p>
                      {a.company_name && <p className="text-xs text-slate-500">{a.company_name}</p>}
                    </div>
                  </div>
                  {a.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone size={11} />{a.phone}</p>}
                  {a.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail size={11} />{a.email}</p>}
                </div>
              ))}
              {assessors.length === 0 && <p className="col-span-full text-center py-8 text-slate-400">No assessors yet</p>}
            </div>
          )}
          {tab === 'Claims' && (
            <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-slate-50">
                  {['Claim #', 'Client', 'Company', 'Status', 'Amount', 'Approved'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">{h}</th>)}
                </tr></thead>
                <tbody>
                  {claims.map(c => (
                    <tr key={c._id} className="border-t border-[#E2E2DF] hover:bg-slate-50 cursor-pointer" onClick={() => openForm('claim', c)}>
                      <td className="px-4 py-3 text-sm font-medium">{c.claim_number || '-'}</td>
                      <td className="px-4 py-3 text-sm">{c.client_name || '-'}</td>
                      <td className="px-4 py-3 text-sm">{c.company_name || '-'}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.claim_status === 'Approved' ? 'bg-green-50 text-green-700' : c.claim_status === 'Declined' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{c.claim_status}</span></td>
                      <td className="px-4 py-3 text-sm">{c.claim_amount ? `$${Number(c.claim_amount).toLocaleString()}` : '-'}</td>
                      <td className="px-4 py-3 text-sm">{c.approved_amount ? `$${Number(c.approved_amount).toLocaleString()}` : '-'}</td>
                    </tr>
                  ))}
                  {claims.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No claims yet</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E2E2DF]">
              <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">{form._id ? 'Edit' : 'New'} {formType === 'company' ? 'Company' : formType === 'assessor' ? 'Assessor' : 'Claim'}</h2>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {formType === 'assessor' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Insurance Company</label>
                  <select value={form.insurance_company_id || ''} onChange={e => setForm({...form, insurance_company_id: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm">
                    <option value="">Select Company</option>
                    {companies.map(c => <option key={c._id} value={c._id}>{c.company_name}</option>)}
                  </select>
                </div>
              )}
              {formType === 'claim' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Insurance Company</label>
                  <select value={form.insurance_company_id || ''} onChange={e => setForm({...form, insurance_company_id: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm">
                    <option value="">Select Company</option>
                    {companies.map(c => <option key={c._id} value={c._id}>{c.company_name}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(formType === 'company' ? companyFields : formType === 'assessor' ? assessorFields : claimFields).map(f => (
                  <div key={f.k} className={f.t === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{f.l}</label>
                    {f.type === 'select' ? (
                      <select value={form[f.k] || ''} onChange={e => setForm({...form, [f.k]: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm">
                        <option value="">Select</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.t === 'textarea' ? (
                      <textarea value={form[f.k] || ''} onChange={e => setForm({...form, [f.k]: e.target.value})} rows={3} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                    ) : (
                      <input type={f.t || 'text'} step={f.t === 'number' ? '0.01' : undefined} required={f.r} value={form[f.k] || ''} onChange={e => setForm({...form, [f.k]: f.t === 'number' ? parseFloat(e.target.value) || 0 : e.target.value})} onBlur={PHONE_KEYS.has(f.k) ? () => setForm(s => ({ ...s, [f.k]: formatNzPhone(s[f.k] || '') })) : undefined} placeholder={PHONE_KEYS.has(f.k) ? '(07) 855 1234' : undefined} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50">Cancel</button>
                <button data-testid="save-insurance-button" type="submit" className="px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
